import * as React from 'react';

import { StatsigProvider } from '@coursera-web/statsig';

import { useLocation } from 'js/lib/useRouter';

import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import EnterpriseHomeApp from 'bundles/program-home/components/enterprise-home/EnterpriseHomeApp';
import { BROWSE_CATEGORY_SLUGS } from 'bundles/unified-common/constants/enterprise';
import { useIsOrgScopedHome } from 'bundles/unified-common/hooks/useIsOrgScopedHome';
import EnterpriseOrgScopedPageDataProvider from 'bundles/unified-common/providers/org-scoped/data/EnterpriseOrgScopedPageDataProvider';
import { normalizeSearchString, searchQueryWithoutSource } from 'bundles/unified-common/utils/orgSearchQuery';

const PROGRAMS_ALL_PREFIX = '/programs/all/';

/** When present in the URL, skip redirect to /organizations so the legacy /programs/all page can be reached (e.g. preview/QA). */
const USE_LEGACY_PROGRAMS_ALL_PARAM = 'useLegacyProgramsAll';

function isLegacyProgramsAllRouteCheck(search: string): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const isLegacyCheck = new URLSearchParams(search).get(USE_LEGACY_PROGRAMS_ALL_PARAM) !== null;
  return isLegacyCheck;
}

/**
 * Route handler for programs/all. When orgScopedHome is on, redirects to /organizations/:orgSlug/...
 * unless the legacy-route param is in the query (so preview/QA can still reach the legacy URL).
 * Consumer and org search use path /search; program search uses /browse for historical reasons.
 * We map /browse → /search and drop the source param. When the flag is off, renders children.
 */
export function buildProgramsAllRedirectUrl(pathname: string, search: string): string | null {
  if (!pathname.startsWith(PROGRAMS_ALL_PREFIX)) return null;
  const afterPrefix = pathname.slice(PROGRAMS_ALL_PREFIX.length);
  const [orgSlug, ...rest] = afterPrefix.split('/');
  const subpath = rest.length > 0 ? `/${rest.join('/')}` : '';
  const firstSegment = rest[0] || '';
  const rawSearch = search || '';
  const isSearch = subpath === '/browse' || subpath === '/browse/';
  const isBrowseCategory = BROWSE_CATEGORY_SLUGS.has(firstSegment);

  let orgPath: string;
  if (isSearch) {
    orgPath = `/organizations/${orgSlug}/search`;
  } else if (isBrowseCategory) {
    // Map domain category to org-scoped browse; strip subdomain if present
    orgPath = `/organizations/${orgSlug}/browse/${firstSegment}`;
  } else {
    orgPath = `/organizations/${orgSlug}${subpath}`;
  }
  const searchPart = isSearch ? searchQueryWithoutSource(rawSearch) : normalizeSearchString(rawSearch);
  return searchPart ? `${orgPath}${searchPart}` : orgPath;
}

type ProgramsAllRouteHandlerProps = {
  children?: React.ReactNode;
  params?: { orgSlug: string };
  [key: string]: unknown;
};

export const ProgramsAllRouteHandler = (props: ProgramsAllRouteHandlerProps) => {
  const isOrgScopedHomeEnabled = useIsOrgScopedHome();
  const { pathname, search } = useLocation();
  const isLegacyProgramsAllRoute = isLegacyProgramsAllRouteCheck(search || '');

  const shouldRedirect =
    isOrgScopedHomeEnabled && !isLegacyProgramsAllRoute && pathname.startsWith(PROGRAMS_ALL_PREFIX);
  const redirectUrl = shouldRedirect ? buildProgramsAllRedirectUrl(pathname, search || '') : null;

  if (redirectUrl) {
    redirectClientOrServer(redirectUrl);
    return null;
  }

  return <EnterpriseHomeApp {...props}>{props.children}</EnterpriseHomeApp>;
};

const ProgramsAllRouteWithProviders = (props: ProgramsAllRouteHandlerProps) => {
  return (
    <StatsigProvider>
      <EnterpriseOrgScopedPageDataProvider>
        <ProgramsAllRouteHandler {...props} />
      </EnterpriseOrgScopedPageDataProvider>
    </StatsigProvider>
  );
};

export default ProgramsAllRouteWithProviders;
